export const TROUSER_V2_FORMULA_VERSION = 'siy_v2';
export const TROUSER_V3_FORMULA_VERSION = 'siy_v3';
export const TROUSER_ACTIVE_FORMULA_VERSION = 'siy_v4';

const roundToSixteenth = (value) => Math.round((Number(value) || 0) / 0.0625) * 0.0625;

const normalizeTrouserPointDeltaMap = (rawPointDeltas = {}) => Object.entries(
  rawPointDeltas && typeof rawPointDeltas === 'object' ? rawPointDeltas : {},
).reduce((acc, [pointName, delta]) => {
  if (!pointName || !delta || typeof delta !== 'object') return acc;
  const x = roundToSixteenth(delta.x);
  const y = roundToSixteenth(delta.y);
  if (Math.abs(x) < 1e-8 && Math.abs(y) < 1e-8) return acc;
  acc[pointName] = { x, y };
  return acc;
}, {});

const normalizeTrouserLengthMode = (value = 'full') => {
  const normalized = String(value || 'full').toLowerCase();
  return normalized === 'short' || normalized === 'shorts' ? 'short' : 'full';
};

const normalizeTrouserPleatDirection = (value = 'reverse') => {
  const normalized = String(value || 'reverse').toLowerCase();
  if (normalized === 'forward' || normalized === 'both') return normalized;
  return 'reverse';
};

const quadraticPoint = (p0, p1, p2, t) => {
  const inv = 1 - t;
  return {
    x: inv * inv * p0.x + 2 * inv * t * p1.x + t * t * p2.x,
    y: inv * inv * p0.y + 2 * inv * t * p1.y + t * t * p2.y,
  };
};

const quadraticCurveLength = (p0, p1, p2, steps = 64) => {
  let length = 0;
  let prev = p0;

  for (let i = 1; i <= steps; i += 1) {
    const point = quadraticPoint(p0, p1, p2, i / steps);
    length += Math.hypot(point.x - prev.x, point.y - prev.y);
    prev = point;
  }

  return length;
};

const cubicPoint = (p0, p1, p2, p3, t) => {
  const inv = 1 - t;
  return {
    x: (inv ** 3) * p0.x + 3 * (inv ** 2) * t * p1.x + 3 * inv * (t ** 2) * p2.x + (t ** 3) * p3.x,
    y: (inv ** 3) * p0.y + 3 * (inv ** 2) * t * p1.y + 3 * inv * (t ** 2) * p2.y + (t ** 3) * p3.y,
  };
};

const cubicCurveLength = (p0, p1, p2, p3, steps = 96) => {
  let length = 0;
  let prev = p0;

  for (let i = 1; i <= steps; i += 1) {
    const point = cubicPoint(p0, p1, p2, p3, i / steps);
    length += Math.hypot(point.x - prev.x, point.y - prev.y);
    prev = point;
  }

  return length;
};

function generateTrouserPatternCore(measurements, controls = {}, options = {}) {
  const {
    decoupleFrontThighFromCrease = false,
    supportsPleats = false,
    formulaVersion = TROUSER_V2_FORMULA_VERSION,
    cbSeatBaselineX = 0,
    backThighOffsetFromHipSide = false,
    frontThighOffsetFromHipSideExact = false,
    integrateBackDartsIntoOutline = false,
    hemShoeShapeSupported = false,
    useV4BackSeamWalkSolver = false,
  } = options;

  const {
    waist,
    seat,
    rise,
    outseam,
    thigh,
    knee,
    hem,
    frontKnee,
    frontHem,

    forkWidth,
    cfDrop,
    creaseOffset,
    backCreaseOffset = 0,
    frontThighSideOffset = 0,
    frontWaistAdd = 0.375,
    frontHipAdd = -0.375,

    cbSeatOffset_X = -1.0,
    cbSeatOffset_Y = 0.75,
    backThighSideOffset = 0.75,
    cbWaistHeight = 1.5,
    cbWaistOffsetX = 0,
    backDartWidth = 1.5,
    backDartDepth = 4.5,
    backDartMode,
    useTwoBackDarts = false,
    backDartSpacing = 2.25,
    backWaistAdd = -0.375,
    backHipAdd = 0.375,

    frontWaistToHipSideCtrlDx = 0,
    frontWaistToHipSideCtrlDy = 0,
    backWaistToHipSideCtrlDx = 0,
    backWaistToHipSideCtrlDy = 0,
    frontSeatToThighSideCtrlDx = 0,
    frontSeatToThighSideCtrlDy = 0,
    backSeatToThighSideCtrlDx = 0,
    backSeatToThighSideCtrlDy = 0,
    frontThighToKneeSideCtrlDx = 0,
    frontThighToKneeSideCtrlDy = 0,
    frontThighToKneeSideCtrlStartDx,
    frontThighToKneeSideCtrlStartDy,
    frontThighToKneeSideCtrlEndDx,
    frontThighToKneeSideCtrlEndDy,
    backThighToKneeSideCtrlDx = 0,
    backThighToKneeSideCtrlDy = 0,
    backThighToKneeSideCtrlStartDx,
    backThighToKneeSideCtrlStartDy,
    backThighToKneeSideCtrlEndDx,
    backThighToKneeSideCtrlEndDy,
    frontInseamThighToKneeCtrlDx = 0,
    frontInseamThighToKneeCtrlDy = 0,
    backInseamThighToKneeCtrlDx = 0,
    backInseamThighToKneeCtrlDy = 0,
    frontCrotchCurveCtrlDx = 0,
    frontCrotchCurveCtrlDy = 0,
    backWaistCenterCurveCtrlDx = 0,
    backWaistCenterCurveCtrlDy = 0,
    backCrotchCurveCtrlDx = 0,
    backCrotchCurveCtrlDy = 0,

    hipLevelInput,
    kneeLevelInput,
    trouserLengthMode = 'full',

    enablePleats = false,
    pleatDirection = 'reverse',
    pleatCount = 1,
    pleatDepth = 1.75,
    secondPleatSpacing = 1.5,
    secondPleatDepth = 1.25,
    hemShoeShapeEnabled = false,
    hemStepValue = 0.5,
  } = measurements;

  const {
    backCrotchScoopX = -0.25,
    backCrotchScoopY = 1.0,
  } = controls;
  const freeModeActive = Boolean(controls?.freeModeActive);
  const freePointDeltas = freeModeActive
    ? normalizeTrouserPointDeltaMap(controls?.pointDeltas || {})
    : {};
  const applyPointDeltaInPlace = (pointName, point) => {
    const delta = freePointDeltas[pointName];
    if (!delta || !point) return point;
    point.x = (Number(point.x) || 0) + delta.x;
    point.y = (Number(point.y) || 0) + delta.y;
    return point;
  };
  const supportsBackWaistCenterCurve = formulaVersion === TROUSER_ACTIVE_FORMULA_VERSION;
  const shortsSupported = formulaVersion === TROUSER_ACTIVE_FORMULA_VERSION;
  const usesV4HemSplit = formulaVersion === TROUSER_ACTIVE_FORMULA_VERSION;
  const isShortMode = shortsSupported && normalizeTrouserLengthMode(trouserLengthMode) === 'short';

  const waistY = outseam;
  const hemY = 0;
  const hipY = outseam - hipLevelInput;
  const crotchY = outseam - rise;
  const kneeY = outseam - kneeLevelInput;

  const fWaistWidth = waist / 4 + frontWaistAdd;
  const fHipWidth = seat / 4 + frontHipAdd;
  const fThighWidth = fHipWidth + forkWidth;
  const resolvedFrontKnee = Number.isFinite(Number(frontKnee)) ? Number(frontKnee) : (knee / 2 - 1.0);
  const safeFrontKnee = Math.max(0.125, Math.min(Math.max(0.125, Number(knee || 0) - 0.125), resolvedFrontKnee));
  const fKneeWidth = safeFrontKnee;

  const resolvedFrontHem = Number.isFinite(Number(frontHem))
    ? Number(frontHem)
    : (hem / 2 - (usesV4HemSplit ? 0.75 : 1.0));
  const safeFrontHem = Math.max(0.125, Math.min(Math.max(0.125, Number(hem || 0) - 0.125), resolvedFrontHem));
  const fHemWidth = safeFrontHem;

  const resolvedBackDartMode = (() => {
    if (backDartMode === 'none' || backDartMode === 'one' || backDartMode === 'two') return backDartMode;
    return useTwoBackDarts ? 'two' : 'one';
  })();

  const dartsEnabled = resolvedBackDartMode !== 'none';
  const twoDartsEnabled = resolvedBackDartMode === 'two';
  const resolvedBackDartSpacing = Number(backDartSpacing) || 2.25;

  const bWaistWidth = waist / 4 + backWaistAdd + (dartsEnabled ? backDartWidth : 0);
  const bHipWidth = seat / 4 + backHipAdd;
  const bKneeWidth = Math.max(0.125, Number(knee || 0) - fKneeWidth);
  const bHemWidth = Math.max(0.125, Number(hem || 0) - fHemWidth);
  const bThighWidth = thigh - fThighWidth;

  const pF_Hem_L = { x: 0, y: hemY };
  const pF_Hem_R = { x: fHemWidth, y: hemY };

  const fCclBaseX = fHemWidth / 2;
  const frontLowerCenterX = decoupleFrontThighFromCrease ? fCclBaseX : fCclBaseX + creaseOffset;
  const upperBlockShiftX = decoupleFrontThighFromCrease ? creaseOffset : 0;
  const fCclX = frontLowerCenterX;

  const pF_Knee_L = isShortMode
    ? { ...pF_Hem_L }
    : { x: frontLowerCenterX - fKneeWidth / 2, y: kneeY };
  const pF_Knee_R = isShortMode
    ? { ...pF_Hem_R }
    : { x: frontLowerCenterX + fKneeWidth / 2, y: kneeY };

  const normalizedPleatCount = Math.max(1, Math.min(2, Number(pleatCount) || 1));
  const pleatEnabled = supportsPleats && Boolean(enablePleats);
  const resolvedPleatDirection = normalizeTrouserPleatDirection(pleatDirection);
  const primaryPleatDepth = Number(pleatDepth) || 0;
  const secondaryPleatDepth = Number(secondPleatDepth) || 0;
  const totalPleatExtension = pleatEnabled
    ? (normalizedPleatCount === 1 ? primaryPleatDepth : primaryPleatDepth + secondaryPleatDepth)
    : 0;
  const pleatSideShare = resolvedPleatDirection === 'forward'
    ? 0
    : (resolvedPleatDirection === 'both' ? 0.5 : 1);
  const pleatCenterShare = resolvedPleatDirection === 'reverse'
    ? 0
    : (resolvedPleatDirection === 'both' ? 0.5 : 1);
  const sidePleatExtension = totalPleatExtension * pleatSideShare;
  const centerPleatExtension = totalPleatExtension * pleatCenterShare;
  const sideShiftAtY = (y) => {
    if (!pleatEnabled || waistY === 0) return 0;
    return sidePleatExtension * (y / waistY);
  };
  const centerShiftAtY = (y) => {
    if (!pleatEnabled || waistY === 0) return 0;
    return centerPleatExtension * (Math.max(0, Number(y) || 0) / waistY);
  };

  const halfFThigh = fThighWidth / 2;

  let pF_Hip_Side;
  let pF_Crotch_Side;

  if (decoupleFrontThighFromCrease) {
    const frontHipSideBaseX = fHemWidth / 2 - halfFThigh - 0.25 + upperBlockShiftX;
    pF_Hip_Side = { x: frontHipSideBaseX, y: hipY };
    pF_Crotch_Side = {
      x: pF_Hip_Side.x + (frontThighOffsetFromHipSideExact ? frontThighSideOffset : (0.25 + frontThighSideOffset)),
      y: crotchY,
    };
  } else {
    pF_Crotch_Side = { x: fCclX - halfFThigh + frontThighSideOffset, y: crotchY };
    pF_Hip_Side = { x: pF_Crotch_Side.x - 0.25, y: hipY };
  }

  const pF_Crotch_Tip = { x: pF_Crotch_Side.x + fThighWidth, y: crotchY };
  const pF_Hip_CF = { x: pF_Hip_Side.x + fHipWidth, y: hipY };
  const pF_Hip_Side_Base = { ...pF_Hip_Side };
  const pF_Crotch_Side_Base = { ...pF_Crotch_Side };
  const pF_Knee_L_Base = { ...pF_Knee_L };
  const frontHipCfForBackX = decoupleFrontThighFromCrease ? pF_Hip_CF.x - upperBlockShiftX : pF_Hip_CF.x;

  const pF_Waist_CF = { x: pF_Hip_CF.x - 0.5, y: waistY - cfDrop };
  const pF_Waist_Side = { x: pF_Waist_CF.x - fWaistWidth, y: waistY };

  if (pleatEnabled) {
    pF_Waist_Side.x -= sideShiftAtY(waistY);
    pF_Hip_Side.x -= sideShiftAtY(hipY);
    pF_Crotch_Side.x -= sideShiftAtY(crotchY);
    pF_Knee_L.x -= sideShiftAtY(kneeY);
    pF_Waist_CF.x += centerShiftAtY(pF_Waist_CF.y);
    pF_Hip_CF.x += centerShiftAtY(hipY);
    pF_Crotch_Tip.x += centerShiftAtY(crotchY);
    pF_Knee_R.x += centerShiftAtY(kneeY);
  }

  const pF_Hip_Side_Measure = pleatEnabled ? pF_Hip_Side_Base : pF_Hip_Side;
  const pF_Crotch_Side_Measure = pleatEnabled ? pF_Crotch_Side_Base : pF_Crotch_Side;
  const pF_Knee_L_Measure = pleatEnabled ? pF_Knee_L_Base : pF_Knee_L;

  const pF_Waist_CP = {
    x: (pF_Waist_CF.x + pF_Waist_Side.x) / 2,
    y: (pF_Waist_CF.y + pF_Waist_Side.y) / 2 - 0.25,
  };

  const pB_Hem_L = { x: pF_Hem_L.x, y: hemY };
  const pB_Hem_R = { x: pB_Hem_L.x + bHemWidth, y: hemY };

  const shoeShapeActive = hemShoeShapeSupported && Boolean(hemShoeShapeEnabled);
  const resolvedHemStepValue = Math.max(0, Number(hemStepValue) || 0);
  const halfHemStepValue = resolvedHemStepValue / 2;
  const fCclHemY = shoeShapeActive ? (pF_Hem_L.y + halfHemStepValue) : pF_Hem_L.y;
  const bCclHemY = shoeShapeActive ? (pB_Hem_L.y - halfHemStepValue) : pB_Hem_L.y;

  const bCclBaseX = pB_Hem_L.x + bHemWidth / 2;
  const backLowerCenterX = bCclBaseX;
  const backUpperBlockShiftX = Number(backCreaseOffset) || 0;
  const bCclX = backLowerCenterX;
  let pB_Knee_L = isShortMode
    ? { ...pB_Hem_L }
    : { x: bCclX - bKneeWidth / 2, y: kneeY };
  let pB_Knee_R = isShortMode
    ? { ...pB_Hem_R }
    : { x: bCclX + bKneeWidth / 2, y: kneeY };

  const backHipCbBaseX = frontHipCfForBackX + backUpperBlockShiftX;
  let pB_Hip_CB = {
    x: backHipCbBaseX + cbSeatBaselineX + cbSeatOffset_X,
    y: hipY + cbSeatOffset_Y,
  };

  const dy_Hip = pB_Hip_CB.y - hipY;
  const dx_Hip = Math.sqrt(Math.max(0, Math.pow(bHipWidth, 2) - Math.pow(dy_Hip, 2)));
  const backHipSideBaseX = backHipCbBaseX - dx_Hip;
  let pB_Hip_Side = { x: backHipSideBaseX + cbSeatBaselineX + cbSeatOffset_X, y: hipY };

  const pB_Waist_CB = { x: backHipCbBaseX - 1.5 + cbWaistOffsetX, y: waistY + cbWaistHeight };

  const dy_Waist = pB_Waist_CB.y - waistY;
  const dx_Waist = Math.sqrt(Math.max(0, Math.pow(bWaistWidth, 2) - Math.pow(dy_Waist, 2)));
  const pB_Waist_Side = { x: pB_Waist_CB.x - dx_Waist, y: waistY };

  let pB_Crotch_Side = {
    x: (backThighOffsetFromHipSide ? pB_Hip_Side.x : backHipSideBaseX) + backThighSideOffset,
    y: crotchY,
  };

  const solveDownwardPointY = (startPoint, endX, targetLength, fallbackY) => {
    const safeTarget = Number(targetLength) || 0;
    const dx = Number(endX) - Number(startPoint.x);
    const minLength = Math.abs(dx);
    const resolvedTarget = Math.max(minLength, safeTarget);
    const dy = Math.sqrt(Math.max(0, resolvedTarget * resolvedTarget - dx * dx));
    const resolvedY = Number(startPoint.y) - dy;
    return Number.isFinite(resolvedY) ? resolvedY : fallbackY;
  };

  const segmentDistance = (p1, p2) => Math.hypot(p2.x - p1.x, p2.y - p1.y);

  const frontPleatNeutralWaistSide = pleatEnabled ? { x: pF_Waist_Side.x + sideShiftAtY(waistY), y: pF_Waist_Side.y } : pF_Waist_Side;
  const frontPleatNeutralHipSide = pleatEnabled ? pF_Hip_Side_Base : pF_Hip_Side;
  const frontPleatNeutralCrotchSide = pleatEnabled ? pF_Crotch_Side_Base : pF_Crotch_Side;
  const frontPleatNeutralKneeLeft = pleatEnabled ? pF_Knee_L_Base : pF_Knee_L;

  const frontSideWaistToHipTargetBase = segmentDistance(frontPleatNeutralWaistSide, frontPleatNeutralHipSide);
  const frontSideHipToThighTargetBase = segmentDistance(frontPleatNeutralHipSide, frontPleatNeutralCrotchSide);
  const frontSideThighToKneeTargetBase = segmentDistance(frontPleatNeutralCrotchSide, frontPleatNeutralKneeLeft);
  const frontSideWaistToHipTarget = pleatEnabled ? frontSideWaistToHipTargetBase : segmentDistance(pF_Waist_Side, pF_Hip_Side);
  const frontSideHipToThighTarget = pleatEnabled ? frontSideHipToThighTargetBase : segmentDistance(pF_Hip_Side, pF_Crotch_Side);
  const frontSideThighToKneeTarget = pleatEnabled ? frontSideThighToKneeTargetBase : segmentDistance(pF_Crotch_Side, pF_Knee_L);

  const resolvedBackHipSideBaseY = solveDownwardPointY(pB_Waist_Side, backHipSideBaseX, frontSideWaistToHipTarget, pB_Hip_Side.y);

  pB_Hip_Side = {
    ...pB_Hip_Side,
    y: resolvedBackHipSideBaseY,
  };

  pB_Hip_CB = {
    ...pB_Hip_CB,
    y: pB_Hip_Side.y + cbSeatOffset_Y,
  };

  pB_Crotch_Side = {
    ...pB_Crotch_Side,
    y: solveDownwardPointY(
      {
        x: backThighOffsetFromHipSide ? pB_Hip_Side.x : backHipSideBaseX,
        y: resolvedBackHipSideBaseY,
      },
      pB_Crotch_Side.x,
      frontSideHipToThighTarget,
      pB_Crotch_Side.y,
    ),
  };

  pB_Knee_L = {
    ...pB_Knee_L,
    y: solveDownwardPointY(pB_Crotch_Side, pB_Knee_L.x, frontSideThighToKneeTarget, pB_Knee_L.y),
  };
  pB_Knee_R = {
    ...pB_Knee_R,
    y: pB_Knee_L.y,
  };

  const distFInseam = Math.hypot(pF_Knee_R.x - pF_Crotch_Tip.x, pF_Knee_R.y - pF_Crotch_Tip.y);

  const bCrotchTipX = pB_Crotch_Side.x + bThighWidth;
  const dxInseam = bCrotchTipX - pB_Knee_R.x;
  const targetBackInseam = Math.max(Math.abs(dxInseam) + 1e-6, distFInseam - 0.125);
  const dyInseamSq = targetBackInseam * targetBackInseam - dxInseam * dxInseam;

  let bCrotchTipY = crotchY;
  if (dyInseamSq > 0) {
    bCrotchTipY = pB_Knee_R.y + Math.sqrt(dyInseamSq);
  }

  const pB_Crotch_Tip = { x: bCrotchTipX, y: bCrotchTipY };

  [
    ['FWC', pF_Waist_CF],
    ['FWS', pF_Waist_Side],
    ['FCP', pF_Waist_CP],
    ['FHS', pF_Hip_Side],
    ['FHC', pF_Hip_CF],
    ['FCS', pF_Crotch_Side],
    ['FCT', pF_Crotch_Tip],
    ['FKL', pF_Knee_L],
    ['FKR', pF_Knee_R],
    ['FHL', pF_Hem_L],
    ['FHR', pF_Hem_R],
    ['BWC', pB_Waist_CB],
    ['BWS', pB_Waist_Side],
    ['BHS', pB_Hip_Side],
    ['BHC', pB_Hip_CB],
    ['BCS', pB_Crotch_Side],
    ['BCT', pB_Crotch_Tip],
    ['BKL', pB_Knee_L],
    ['BKR', pB_Knee_R],
    ['BHL', pB_Hem_L],
    ['BHR', pB_Hem_R],
  ].forEach(([pointName, point]) => applyPointDeltaInPlace(pointName, point));

  const pF_Crease_Waist = applyPointDeltaInPlace('FCLW', { x: fCclX, y: waistY });
  const pF_Crease_Hem = applyPointDeltaInPlace('FCLH', { x: fCclX, y: fCclHemY });
  const pB_Crease_Waist = applyPointDeltaInPlace('BCLW', { x: bCclX, y: waistY });
  const pB_Crease_Hem = applyPointDeltaInPlace('BCLH', { x: bCclX, y: bCclHemY });
  const resolvedFrontNeutralWaistSide = pleatEnabled
    ? { x: pF_Waist_Side.x + sideShiftAtY(waistY), y: pF_Waist_Side.y }
    : { ...pF_Waist_Side };
  const resolvedFrontNeutralWaistCf = pleatEnabled
    ? { x: pF_Waist_CF.x - centerShiftAtY(pF_Waist_CF.y), y: pF_Waist_CF.y }
    : { ...pF_Waist_CF };
  const resolvedFrontNeutralHipSide = pleatEnabled
    ? { x: pF_Hip_Side.x + sideShiftAtY(hipY), y: pF_Hip_Side.y }
    : { ...pF_Hip_Side };
  const resolvedFrontNeutralHipCf = pleatEnabled
    ? { x: pF_Hip_CF.x - centerShiftAtY(hipY), y: pF_Hip_CF.y }
    : { ...pF_Hip_CF };
  const resolvedFrontNeutralCrotchSide = pleatEnabled
    ? { x: pF_Crotch_Side.x + sideShiftAtY(crotchY), y: pF_Crotch_Side.y }
    : { ...pF_Crotch_Side };
  const resolvedFrontNeutralCrotchTip = pleatEnabled
    ? { x: pF_Crotch_Tip.x - centerShiftAtY(crotchY), y: pF_Crotch_Tip.y }
    : { ...pF_Crotch_Tip };
  const resolvedFrontNeutralKneeLeft = pleatEnabled
    ? { x: pF_Knee_L.x + sideShiftAtY(kneeY), y: pF_Knee_L.y }
    : { ...pF_Knee_L };
  const resolvedFrontNeutralKneeRight = pleatEnabled
    ? { x: pF_Knee_R.x - centerShiftAtY(kneeY), y: pF_Knee_R.y }
    : { ...pF_Knee_R };

  const buildBackDartsFromWaistLine = () => {
    if (!dartsEnabled) return [];

    const pWaistMid = {
      x: (pB_Waist_CB.x + pB_Waist_Side.x) / 2,
      y: (pB_Waist_CB.y + pB_Waist_Side.y) / 2,
    };
    const dx = pB_Waist_CB.x - pB_Waist_Side.x;
    const dy = pB_Waist_CB.y - pB_Waist_Side.y;
    const len = Math.hypot(dx, dy) || 1;
    const u = { x: dx / len, y: dy / len };
    const perp = { x: u.y, y: -u.x };

    const buildDart = (centerPoint, width) => {
      const halfWidth = width / 2;
      return {
        leg1: { x: centerPoint.x - u.x * halfWidth, y: centerPoint.y - u.y * halfWidth },
        leg2: { x: centerPoint.x + u.x * halfWidth, y: centerPoint.y + u.y * halfWidth },
        tip: { x: centerPoint.x + perp.x * backDartDepth, y: centerPoint.y + perp.y * backDartDepth },
      };
    };

    const builtDarts = [];

    if (twoDartsEnabled) {
      const centerOffset = resolvedBackDartSpacing / 2;
      const eachDartWidth = backDartWidth / 2;

      const dartCenter1 = { x: pWaistMid.x - u.x * centerOffset, y: pWaistMid.y - u.y * centerOffset };
      const dartCenter2 = { x: pWaistMid.x + u.x * centerOffset, y: pWaistMid.y + u.y * centerOffset };

      builtDarts.push(buildDart(dartCenter1, eachDartWidth));
      builtDarts.push(buildDart(dartCenter2, eachDartWidth));
    } else {
      builtDarts.push(buildDart(pWaistMid, backDartWidth));
    }

    return builtDarts;
  };

  let darts = [];

  const toSVG = (p) => `${p.x} ${-p.y}`;

  const normalizeOrFallback = (vector, fallback = { x: 1, y: 0 }) => {
    const length = Math.hypot(vector.x, vector.y);
    if (length < 1e-6) return fallback;
    return {
      x: vector.x / length,
      y: vector.y / length,
    };
  };

  const smoothQuadraticTurnPoint = ({
    prevPoint,
    turnPoint,
    nextPoint,
    incomingControl,
    outgoingControl,
  }) => {
    const incomingLength = Math.max(0.125, Math.hypot(turnPoint.x - incomingControl.x, turnPoint.y - incomingControl.y));
    const outgoingLength = Math.max(0.125, Math.hypot(outgoingControl.x - turnPoint.x, outgoingControl.y - turnPoint.y));

    const incomingTangent = normalizeOrFallback({
      x: turnPoint.x - incomingControl.x,
      y: turnPoint.y - incomingControl.y,
    }, normalizeOrFallback({
      x: turnPoint.x - prevPoint.x,
      y: turnPoint.y - prevPoint.y,
    }, { x: 0, y: 1 }));

    const outgoingTangent = normalizeOrFallback({
      x: outgoingControl.x - turnPoint.x,
      y: outgoingControl.y - turnPoint.y,
    }, normalizeOrFallback({
      x: nextPoint.x - turnPoint.x,
      y: nextPoint.y - turnPoint.y,
    }, { x: 0, y: 1 }));

    const blendedTangent = normalizeOrFallback({
      x: incomingTangent.x + outgoingTangent.x,
      y: incomingTangent.y + outgoingTangent.y,
    }, normalizeOrFallback({
      x: nextPoint.x - prevPoint.x,
      y: nextPoint.y - prevPoint.y,
    }, { x: 0, y: 1 }));

    return {
      incomingControl: {
        x: turnPoint.x - blendedTangent.x * incomingLength,
        y: turnPoint.y - blendedTangent.y * incomingLength,
      },
      outgoingControl: {
        x: turnPoint.x + blendedTangent.x * outgoingLength,
        y: turnPoint.y + blendedTangent.y * outgoingLength,
      },
    };
  };

  const alignQuadraticControlAtEnd = ({ control, endPoint, nextPoint }) => {
    const length = Math.max(0.125, Math.hypot(endPoint.x - control.x, endPoint.y - control.y));
    const tangent = normalizeOrFallback({
      x: nextPoint.x - endPoint.x,
      y: nextPoint.y - endPoint.y,
    }, { x: 0, y: -1 });

    return {
      x: endPoint.x - tangent.x * length,
      y: endPoint.y - tangent.y * length,
    };
  };

  const alignQuadraticControlAtStart = ({ control, startPoint, prevPoint }) => {
    const length = Math.max(0.125, Math.hypot(control.x - startPoint.x, control.y - startPoint.y));
    const tangent = normalizeOrFallback({
      x: startPoint.x - prevPoint.x,
      y: startPoint.y - prevPoint.y,
    }, { x: 0, y: 1 });

    return {
      x: startPoint.x + tangent.x * length,
      y: startPoint.y + tangent.y * length,
    };
  };

  const hasManualOffset = (dx, dy) => {
    const x = Number(dx) || 0;
    const y = Number(dy) || 0;
    return Math.abs(x) > 1e-6 || Math.abs(y) > 1e-6;
  };

  const resolveCubicControls = ({
    startPoint,
    endPoint,
    startDx = 0,
    startDy = 0,
    endDx = 0,
    endDy = 0,
  }) => {
    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    return {
      start: {
        x: startPoint.x + dx * 0.35 + startDx,
        y: startPoint.y + dy * 0.35 + startDy,
      },
      end: {
        x: startPoint.x + dx * 0.75 + endDx,
        y: startPoint.y + dy * 0.75 + endDy,
      },
    };
  };

  const resolveQuadraticControl = (start, end, dx = 0, dy = 0) => ({
    x: (start.x + end.x) / 2 + (Number(dx) || 0),
    y: (start.y + end.y) / 2 + (Number(dy) || 0),
  });

  const cpBackCrotchBase = {
    x: pB_Crotch_Tip.x - bThighWidth * 0.4 * (1 + backCrotchScoopX),
    y: bCrotchTipY + rise * 0.15 * (1 - backCrotchScoopY),
  };

  let frontWaistToHipSideCtrl = resolveQuadraticControl(
    pF_Waist_Side,
    pF_Hip_Side,
    frontWaistToHipSideCtrlDx,
    frontWaistToHipSideCtrlDy,
  );
  let frontSeatToThighSideCtrl = resolveQuadraticControl(
    pF_Hip_Side,
    pF_Crotch_Side,
    frontSeatToThighSideCtrlDx,
    frontSeatToThighSideCtrlDy,
  );
  const frontThighToKneeStartDxResolved = Number.isFinite(Number(frontThighToKneeSideCtrlStartDx))
    ? Number(frontThighToKneeSideCtrlStartDx)
    : (Number(frontThighToKneeSideCtrlDx) || 0);
  const frontThighToKneeStartDyResolved = Number.isFinite(Number(frontThighToKneeSideCtrlStartDy))
    ? Number(frontThighToKneeSideCtrlStartDy)
    : (Number(frontThighToKneeSideCtrlDy) || 0);
  const frontThighToKneeEndDxResolved = Number.isFinite(Number(frontThighToKneeSideCtrlEndDx))
    ? Number(frontThighToKneeSideCtrlEndDx)
    : (Number(frontThighToKneeSideCtrlDx) || 0);
  const frontThighToKneeEndDyResolved = Number.isFinite(Number(frontThighToKneeSideCtrlEndDy))
    ? Number(frontThighToKneeSideCtrlEndDy)
    : (Number(frontThighToKneeSideCtrlDy) || 0);
  const frontThighToKneeSideCubic = resolveCubicControls({
    startPoint: pF_Crotch_Side,
    endPoint: pF_Knee_L,
    startDx: frontThighToKneeStartDxResolved,
    startDy: frontThighToKneeStartDyResolved,
    endDx: frontThighToKneeEndDxResolved,
    endDy: frontThighToKneeEndDyResolved,
  });
  let frontThighToKneeSideCtrlStart = frontThighToKneeSideCubic.start;
  let frontThighToKneeSideCtrlEnd = frontThighToKneeSideCubic.end;
  let frontInseamThighToKneeCtrl = resolveQuadraticControl(
    pF_Knee_R,
    pF_Crotch_Tip,
    frontInseamThighToKneeCtrlDx,
    frontInseamThighToKneeCtrlDy,
  );
  const frontCrotchCurveCtrl = {
    x: pF_Crotch_Tip.x - forkWidth + (Number(frontCrotchCurveCtrlDx) || 0),
    y: crotchY + (Number(frontCrotchCurveCtrlDy) || 0),
  };

  let backWaistToHipSideCtrl = resolveQuadraticControl(
    pB_Waist_Side,
    pB_Hip_Side,
    backWaistToHipSideCtrlDx,
    backWaistToHipSideCtrlDy,
  );
  let backSeatToThighSideCtrl = resolveQuadraticControl(
    pB_Hip_Side,
    pB_Crotch_Side,
    backSeatToThighSideCtrlDx,
    backSeatToThighSideCtrlDy,
  );
  const backThighToKneeStartDxResolved = Number.isFinite(Number(backThighToKneeSideCtrlStartDx))
    ? Number(backThighToKneeSideCtrlStartDx)
    : (Number(backThighToKneeSideCtrlDx) || 0);
  const backThighToKneeStartDyResolved = Number.isFinite(Number(backThighToKneeSideCtrlStartDy))
    ? Number(backThighToKneeSideCtrlStartDy)
    : (Number(backThighToKneeSideCtrlDy) || 0);
  const backThighToKneeEndDxResolved = Number.isFinite(Number(backThighToKneeSideCtrlEndDx))
    ? Number(backThighToKneeSideCtrlEndDx)
    : (Number(backThighToKneeSideCtrlDx) || 0);
  const backThighToKneeEndDyResolved = Number.isFinite(Number(backThighToKneeSideCtrlEndDy))
    ? Number(backThighToKneeSideCtrlEndDy)
    : (Number(backThighToKneeSideCtrlDy) || 0);
  const backThighToKneeSideCubic = resolveCubicControls({
    startPoint: pB_Crotch_Side,
    endPoint: pB_Knee_L,
    startDx: backThighToKneeStartDxResolved,
    startDy: backThighToKneeStartDyResolved,
    endDx: backThighToKneeEndDxResolved,
    endDy: backThighToKneeEndDyResolved,
  });
  let backThighToKneeSideCtrlStart = backThighToKneeSideCubic.start;
  let backThighToKneeSideCtrlEnd = backThighToKneeSideCubic.end;
  let backInseamThighToKneeCtrl = resolveQuadraticControl(
    pB_Knee_R,
    pB_Crotch_Tip,
    backInseamThighToKneeCtrlDx,
    backInseamThighToKneeCtrlDy,
  );
  const cpBackCrotch = {
    x: cpBackCrotchBase.x + (Number(backCrotchCurveCtrlDx) || 0),
    y: cpBackCrotchBase.y + (Number(backCrotchCurveCtrlDy) || 0),
  };
  const backWaistCenterCurveCtrlBase = {
    x: (pB_Hip_CB.x + pB_Waist_CB.x) / 2,
    y: (pB_Hip_CB.y + pB_Waist_CB.y) / 2,
  };
  const backWaistCenterCurveCtrl = supportsBackWaistCenterCurve
    ? {
        x: backWaistCenterCurveCtrlBase.x + (Number(backWaistCenterCurveCtrlDx) || 0),
        y: backWaistCenterCurveCtrlBase.y + (Number(backWaistCenterCurveCtrlDy) || 0),
      }
    : backWaistCenterCurveCtrlBase;

  if (useV4BackSeamWalkSolver) {
    pB_Knee_L.y = pF_Knee_L.y;
    pB_Knee_R.y = pF_Knee_R.y;

    const buildResolvedSideSeamGeometry = ({
      waistSide,
      hipSide,
      crotchSide,
      kneeSide,
      hemSide,
      waistHipDx,
      waistHipDy,
      hipThighDx,
      hipThighDy,
      thighStartDx,
      thighStartDy,
      thighEndDx,
      thighEndDy,
    }) => {
      let waistHipCtrl = resolveQuadraticControl(waistSide, hipSide, waistHipDx, waistHipDy);
      let hipThighCtrl = resolveQuadraticControl(hipSide, crotchSide, hipThighDx, hipThighDy);
      const thighCubic = resolveCubicControls({
        startPoint: crotchSide,
        endPoint: kneeSide,
        startDx: thighStartDx,
        startDy: thighStartDy,
        endDx: thighEndDx,
        endDy: thighEndDy,
      });
      let thighStartCtrl = thighCubic.start;
      let thighEndCtrl = thighCubic.end;

      const hipSmoothLocked = !hasManualOffset(waistHipDx, waistHipDy) && !hasManualOffset(hipThighDx, hipThighDy);
      const thighSmoothLocked = !hasManualOffset(hipThighDx, hipThighDy)
        && !hasManualOffset(thighStartDx, thighStartDy)
        && !hasManualOffset(thighEndDx, thighEndDy);

      if (hipSmoothLocked) {
        ({
          incomingControl: waistHipCtrl,
          outgoingControl: hipThighCtrl,
        } = smoothQuadraticTurnPoint({
          prevPoint: waistSide,
          turnPoint: hipSide,
          nextPoint: crotchSide,
          incomingControl: waistHipCtrl,
          outgoingControl: hipThighCtrl,
        }));
      }

      if (thighSmoothLocked) {
        ({
          incomingControl: hipThighCtrl,
          outgoingControl: thighStartCtrl,
        } = smoothQuadraticTurnPoint({
          prevPoint: hipSide,
          turnPoint: crotchSide,
          nextPoint: kneeSide,
          incomingControl: hipThighCtrl,
          outgoingControl: thighStartCtrl,
        }));
      }

      const thighAlignedBase = alignQuadraticControlAtEnd({
        control: resolveCubicControls({ startPoint: crotchSide, endPoint: kneeSide }).end,
        endPoint: kneeSide,
        nextPoint: hemSide,
      });
      thighEndCtrl = hasManualOffset(thighEndDx, thighEndDy)
        ? {
            x: thighAlignedBase.x + thighEndDx,
            y: thighAlignedBase.y + thighEndDy,
          }
        : thighAlignedBase;

      return {
        waistHipCtrl,
        hipThighCtrl,
        thighStartCtrl,
        thighEndCtrl,
        lengths: {
          waistHip: quadraticCurveLength(waistSide, waistHipCtrl, hipSide),
          hipThigh: quadraticCurveLength(hipSide, hipThighCtrl, crotchSide),
          thighKnee: cubicCurveLength(crotchSide, thighStartCtrl, thighEndCtrl, kneeSide),
        },
      };
    };

    const frontNeutralSeam = buildResolvedSideSeamGeometry({
      waistSide: resolvedFrontNeutralWaistSide,
      hipSide: resolvedFrontNeutralHipSide,
      crotchSide: resolvedFrontNeutralCrotchSide,
      kneeSide: resolvedFrontNeutralKneeLeft,
      hemSide: pF_Hem_L,
      waistHipDx: frontWaistToHipSideCtrlDx,
      waistHipDy: frontWaistToHipSideCtrlDy,
      hipThighDx: frontSeatToThighSideCtrlDx,
      hipThighDy: frontSeatToThighSideCtrlDy,
      thighStartDx: frontThighToKneeStartDxResolved,
      thighStartDy: frontThighToKneeStartDyResolved,
      thighEndDx: frontThighToKneeEndDxResolved,
      thighEndDy: frontThighToKneeEndDyResolved,
    });

    const frontNeutralWaistHipLen = frontNeutralSeam.lengths.waistHip;
    const frontNeutralHipThighLen = frontNeutralSeam.lengths.hipThigh;
    const frontNeutralThighKneeLen = frontNeutralSeam.lengths.thighKnee;

    let solvedWaistY = pB_Waist_Side.y;
    let solvedHipY = pB_Hip_Side.y;
    let solvedCrotchY = pB_Crotch_Side.y;
    const seamTolerance = 1 / 16;

    const getBackSeamFor = (waistYValue, hipYValue, crotchYValue) => buildResolvedSideSeamGeometry({
      waistSide: { ...pB_Waist_Side, y: waistYValue },
      hipSide: { ...pB_Hip_Side, y: hipYValue },
      crotchSide: { ...pB_Crotch_Side, y: crotchYValue },
      kneeSide: { ...pB_Knee_L, y: pF_Knee_L.y },
      hemSide: pB_Hem_L,
      waistHipDx: backWaistToHipSideCtrlDx,
      waistHipDy: backWaistToHipSideCtrlDy,
      hipThighDx: backSeatToThighSideCtrlDx,
      hipThighDy: backSeatToThighSideCtrlDy,
      thighStartDx: backThighToKneeStartDxResolved,
      thighStartDy: backThighToKneeStartDyResolved,
      thighEndDx: backThighToKneeEndDxResolved,
      thighEndDy: backThighToKneeEndDyResolved,
    });

    const evaluateBackSeam = (waistYValue, hipYValue, crotchYValue) => {
      const kneeFloor = pF_Knee_L.y + 0.125;
      if (crotchYValue <= kneeFloor || hipYValue <= crotchYValue + 0.125 || waistYValue <= hipYValue + 0.125) {
        return { score: Number.POSITIVE_INFINITY };
      }

      const seam = getBackSeamFor(waistYValue, hipYValue, crotchYValue);
      const deltas = {
        waistHip: seam.lengths.waistHip - frontNeutralWaistHipLen,
        hipThigh: seam.lengths.hipThigh - frontNeutralHipThighLen,
        thighKnee: seam.lengths.thighKnee - frontNeutralThighKneeLen,
      };

      const score = (deltas.waistHip ** 2) + (deltas.hipThigh ** 2) + (deltas.thighKnee ** 2);
      return { seam, deltas, score };
    };

    let best = {
      waistY: solvedWaistY,
      hipY: solvedHipY,
      crotchY: solvedCrotchY,
      ...evaluateBackSeam(solvedWaistY, solvedHipY, solvedCrotchY),
    };

    for (let waistOffset = -4; waistOffset <= 4; waistOffset += 0.5) {
      for (let hipOffset = -4; hipOffset <= 4; hipOffset += 0.5) {
        for (let crotchOffset = -4; crotchOffset <= 4; crotchOffset += 0.5) {
          const candidate = {
            waistY: solvedWaistY + waistOffset,
            hipY: solvedHipY + hipOffset,
            crotchY: solvedCrotchY + crotchOffset,
          };
          const evaluation = evaluateBackSeam(candidate.waistY, candidate.hipY, candidate.crotchY);
          if (evaluation.score < best.score) {
            best = { ...candidate, ...evaluation };
          }
        }
      }
    }

    const coordinateSteps = [0.5, 0.25, 0.125, 0.0625, 0.03125];
    coordinateSteps.forEach((step) => {
      let improved = true;
      while (improved) {
        improved = false;
        const directions = [
          [step, 0, 0],
          [-step, 0, 0],
          [0, step, 0],
          [0, -step, 0],
          [0, 0, step],
          [0, 0, -step],
          [step, step, 0],
          [step, 0, step],
          [0, step, step],
          [-step, -step, 0],
          [-step, 0, -step],
          [0, -step, -step],
        ];

        directions.forEach(([waistStep, hipStep, crotchStep]) => {
          const candidate = {
            waistY: best.waistY + waistStep,
            hipY: best.hipY + hipStep,
            crotchY: best.crotchY + crotchStep,
          };
          const evaluation = evaluateBackSeam(candidate.waistY, candidate.hipY, candidate.crotchY);
          if (evaluation.score + 1e-9 < best.score) {
            best = { ...candidate, ...evaluation };
            improved = true;
          }
        });
      }
    });

    solvedWaistY = best.waistY;
    solvedHipY = best.hipY;
    solvedCrotchY = best.crotchY;

    pB_Waist_Side.y = solvedWaistY;
    pB_Hip_Side.y = solvedHipY;
    pB_Hip_CB.y = solvedHipY + cbSeatOffset_Y;
    pB_Crotch_Side.y = solvedCrotchY;
    pB_Knee_L.y = pF_Knee_L.y;
    pB_Knee_R.y = pF_Knee_R.y;

    const resolvedBackSeam = buildResolvedSideSeamGeometry({
      waistSide: pB_Waist_Side,
      hipSide: pB_Hip_Side,
      crotchSide: pB_Crotch_Side,
      kneeSide: pB_Knee_L,
      hemSide: pB_Hem_L,
      waistHipDx: backWaistToHipSideCtrlDx,
      waistHipDy: backWaistToHipSideCtrlDy,
      hipThighDx: backSeatToThighSideCtrlDx,
      hipThighDy: backSeatToThighSideCtrlDy,
      thighStartDx: backThighToKneeStartDxResolved,
      thighStartDy: backThighToKneeStartDyResolved,
      thighEndDx: backThighToKneeEndDxResolved,
      thighEndDy: backThighToKneeEndDyResolved,
    });

    backWaistToHipSideCtrl = resolvedBackSeam.waistHipCtrl;
    backSeatToThighSideCtrl = resolvedBackSeam.hipThighCtrl;
    backThighToKneeSideCtrlStart = resolvedBackSeam.thighStartCtrl;
    backThighToKneeSideCtrlEnd = resolvedBackSeam.thighEndCtrl;

    const targetBackInseamV4 = Math.max(Math.abs(dxInseam) + 1e-6, distFInseam - 0.125);
    const adjustedDyInseamSq = targetBackInseamV4 * targetBackInseamV4 - dxInseam * dxInseam;
    pB_Crotch_Tip.y = adjustedDyInseamSq > 0 ? pB_Knee_R.y + Math.sqrt(adjustedDyInseamSq) : crotchY;
    cpBackCrotchBase.y = pB_Crotch_Tip.y + rise * 0.15 * (1 - backCrotchScoopY);
    cpBackCrotch.y = cpBackCrotchBase.y + (Number(backCrotchCurveCtrlDy) || 0);
    backInseamThighToKneeCtrl = resolveQuadraticControl(
      pB_Knee_R,
      pB_Crotch_Tip,
      backInseamThighToKneeCtrlDx,
      backInseamThighToKneeCtrlDy,
    );
    const backInseamAlignedBaseV4 = alignQuadraticControlAtStart({
      control: resolveQuadraticControl(pB_Knee_R, pB_Crotch_Tip, 0, 0),
      startPoint: pB_Knee_R,
      prevPoint: pB_Hem_R,
    });
    backInseamThighToKneeCtrl = hasManualOffset(backInseamThighToKneeCtrlDx, backInseamThighToKneeCtrlDy)
      ? {
          x: backInseamAlignedBaseV4.x + (Number(backInseamThighToKneeCtrlDx) || 0),
          y: backInseamAlignedBaseV4.y + (Number(backInseamThighToKneeCtrlDy) || 0),
        }
      : backInseamAlignedBaseV4;
  }

  darts = buildBackDartsFromWaistLine();

  if (darts[0]) {
    applyPointDeltaInPlace('BD1', darts[0].leg1);
    applyPointDeltaInPlace('BD2', darts[0].leg2);
    applyPointDeltaInPlace('BDT1', darts[0].tip);
  }
  if (darts[1]) {
    applyPointDeltaInPlace('BD3', darts[1].leg1);
    applyPointDeltaInPlace('BD4', darts[1].leg2);
    applyPointDeltaInPlace('BDT2', darts[1].tip);
  }

  const frontHipSmoothLocked = !hasManualOffset(frontWaistToHipSideCtrlDx, frontWaistToHipSideCtrlDy)
    && !hasManualOffset(frontSeatToThighSideCtrlDx, frontSeatToThighSideCtrlDy);
  if (frontHipSmoothLocked) {
    ({
      incomingControl: frontWaistToHipSideCtrl,
      outgoingControl: frontSeatToThighSideCtrl,
    } = smoothQuadraticTurnPoint({
      prevPoint: pF_Waist_Side,
      turnPoint: pF_Hip_Side,
      nextPoint: pF_Crotch_Side,
      incomingControl: frontWaistToHipSideCtrl,
      outgoingControl: frontSeatToThighSideCtrl,
    }));
  }

  const frontThighSmoothLocked = !hasManualOffset(frontSeatToThighSideCtrlDx, frontSeatToThighSideCtrlDy)
    && !hasManualOffset(frontThighToKneeStartDxResolved, frontThighToKneeStartDyResolved)
    && !hasManualOffset(frontThighToKneeEndDxResolved, frontThighToKneeEndDyResolved);
  if (frontThighSmoothLocked) {
    ({
      incomingControl: frontSeatToThighSideCtrl,
      outgoingControl: frontThighToKneeSideCtrlStart,
    } = smoothQuadraticTurnPoint({
      prevPoint: pF_Hip_Side,
      turnPoint: pF_Crotch_Side,
      nextPoint: pF_Knee_L,
      incomingControl: frontSeatToThighSideCtrl,
      outgoingControl: frontThighToKneeSideCtrlStart,
    }));
  }

  const backHipSmoothLocked = !hasManualOffset(backWaistToHipSideCtrlDx, backWaistToHipSideCtrlDy)
    && !hasManualOffset(backSeatToThighSideCtrlDx, backSeatToThighSideCtrlDy);
  if (backHipSmoothLocked) {
    ({
      incomingControl: backWaistToHipSideCtrl,
      outgoingControl: backSeatToThighSideCtrl,
    } = smoothQuadraticTurnPoint({
      prevPoint: pB_Waist_Side,
      turnPoint: pB_Hip_Side,
      nextPoint: pB_Crotch_Side,
      incomingControl: backWaistToHipSideCtrl,
      outgoingControl: backSeatToThighSideCtrl,
    }));
  }

  const backThighSmoothLocked = !hasManualOffset(backSeatToThighSideCtrlDx, backSeatToThighSideCtrlDy)
    && !hasManualOffset(backThighToKneeStartDxResolved, backThighToKneeStartDyResolved)
    && !hasManualOffset(backThighToKneeEndDxResolved, backThighToKneeEndDyResolved);
  if (backThighSmoothLocked) {
    ({
      incomingControl: backSeatToThighSideCtrl,
      outgoingControl: backThighToKneeSideCtrlStart,
    } = smoothQuadraticTurnPoint({
      prevPoint: pB_Hip_Side,
      turnPoint: pB_Crotch_Side,
      nextPoint: pB_Knee_L,
      incomingControl: backSeatToThighSideCtrl,
      outgoingControl: backThighToKneeSideCtrlStart,
    }));
  }

  const frontThighToKneeAlignedBase = alignQuadraticControlAtEnd({
    control: resolveCubicControls({ startPoint: pF_Crotch_Side, endPoint: pF_Knee_L }).end,
    endPoint: pF_Knee_L,
    nextPoint: pF_Hem_L,
  });
  frontThighToKneeSideCtrlEnd = hasManualOffset(frontThighToKneeEndDxResolved, frontThighToKneeEndDyResolved)
    ? {
      x: frontThighToKneeAlignedBase.x + frontThighToKneeEndDxResolved,
      y: frontThighToKneeAlignedBase.y + frontThighToKneeEndDyResolved,
    }
    : frontThighToKneeAlignedBase;

  const backThighToKneeAlignedBase = alignQuadraticControlAtEnd({
    control: resolveCubicControls({ startPoint: pB_Crotch_Side, endPoint: pB_Knee_L }).end,
    endPoint: pB_Knee_L,
    nextPoint: pB_Hem_L,
  });
  backThighToKneeSideCtrlEnd = hasManualOffset(backThighToKneeEndDxResolved, backThighToKneeEndDyResolved)
    ? {
      x: backThighToKneeAlignedBase.x + backThighToKneeEndDxResolved,
      y: backThighToKneeAlignedBase.y + backThighToKneeEndDyResolved,
    }
    : backThighToKneeAlignedBase;

  const frontInseamThighToKneeAlignedBase = alignQuadraticControlAtStart({
    control: resolveQuadraticControl(pF_Knee_R, pF_Crotch_Tip, 0, 0),
    startPoint: pF_Knee_R,
    prevPoint: pF_Hem_R,
  });
  frontInseamThighToKneeCtrl = hasManualOffset(frontInseamThighToKneeCtrlDx, frontInseamThighToKneeCtrlDy)
    ? {
      x: frontInseamThighToKneeAlignedBase.x + (Number(frontInseamThighToKneeCtrlDx) || 0),
      y: frontInseamThighToKneeAlignedBase.y + (Number(frontInseamThighToKneeCtrlDy) || 0),
    }
    : frontInseamThighToKneeAlignedBase;

  const backInseamThighToKneeAlignedBase = alignQuadraticControlAtStart({
    control: resolveQuadraticControl(pB_Knee_R, pB_Crotch_Tip, 0, 0),
    startPoint: pB_Knee_R,
    prevPoint: pB_Hem_R,
  });
  backInseamThighToKneeCtrl = hasManualOffset(backInseamThighToKneeCtrlDx, backInseamThighToKneeCtrlDy)
    ? {
      x: backInseamThighToKneeAlignedBase.x + (Number(backInseamThighToKneeCtrlDx) || 0),
      y: backInseamThighToKneeAlignedBase.y + (Number(backInseamThighToKneeCtrlDy) || 0),
    }
    : backInseamThighToKneeAlignedBase;

  const frontPath = `
    M ${toSVG(pF_Waist_CF)}
    Q ${toSVG(pF_Waist_CP)} ${toSVG(pF_Waist_Side)}
    Q ${toSVG(frontWaistToHipSideCtrl)} ${toSVG(pF_Hip_Side)}
    Q ${toSVG(frontSeatToThighSideCtrl)} ${toSVG(pF_Crotch_Side)}
    C ${toSVG(frontThighToKneeSideCtrlStart)} ${toSVG(frontThighToKneeSideCtrlEnd)} ${toSVG(isShortMode ? pF_Hem_L : pF_Knee_L)}
    ${isShortMode ? '' : `L ${toSVG(pF_Hem_L)}`}
    ${shoeShapeActive ? `L ${toSVG(pF_Crease_Hem)}` : ''}
    L ${toSVG(pF_Hem_R)}
    ${isShortMode ? '' : `L ${toSVG(pF_Knee_R)}`}
    Q ${toSVG(frontInseamThighToKneeCtrl)} ${toSVG(pF_Crotch_Tip)}
    Q ${toSVG(frontCrotchCurveCtrl)} ${toSVG(pF_Hip_CF)}
    L ${toSVG(pF_Waist_CF)}
    Z
  `;

  const backPath = `
    M ${toSVG(pB_Waist_Side)}
    Q ${toSVG(backWaistToHipSideCtrl)} ${toSVG(pB_Hip_Side)}
    Q ${toSVG(backSeatToThighSideCtrl)} ${toSVG(pB_Crotch_Side)}
    C ${toSVG(backThighToKneeSideCtrlStart)} ${toSVG(backThighToKneeSideCtrlEnd)} ${toSVG(isShortMode ? pB_Hem_L : pB_Knee_L)}
    ${isShortMode ? '' : `L ${toSVG(pB_Hem_L)}`}
    ${shoeShapeActive ? `L ${toSVG(pB_Crease_Hem)}` : ''}
    L ${toSVG(pB_Hem_R)}
    ${isShortMode ? '' : `L ${toSVG(pB_Knee_R)}`}
    Q ${toSVG(backInseamThighToKneeCtrl)} ${toSVG(pB_Crotch_Tip)}
    Q ${toSVG(cpBackCrotch)} ${toSVG(pB_Hip_CB)}
    ${supportsBackWaistCenterCurve ? `Q ${toSVG(backWaistCenterCurveCtrl)} ${toSVG(pB_Waist_CB)}` : `L ${toSVG(pB_Waist_CB)}`}
    ${integrateBackDartsIntoOutline && dartsEnabled
      ? [...darts].reverse().map((dart) => `L ${toSVG(dart.leg2)} L ${toSVG(dart.tip)} L ${toSVG(dart.leg1)}`).join(' ')
      : ''}
    L ${toSVG(pB_Waist_Side)}
    Z
  `;

  const dartPath = integrateBackDartsIntoOutline
    ? ''
    : darts.map((dart) => `M ${toSVG(dart.leg1)} L ${toSVG(dart.tip)} L ${toSVG(dart.leg2)} Z`).join(' ');
  const fCreasePath = `M ${toSVG(pF_Crease_Waist)} L ${toSVG(pF_Crease_Hem)}`;
  const bCreasePath = `M ${toSVG(pB_Crease_Waist)} L ${toSVG(pB_Crease_Hem)}`;

  const pleatLines = [];
  if (pleatEnabled) {
    const depth = primaryPleatDepth;
    const spacing = Number(secondPleatSpacing) || 0;
    if (resolvedPleatDirection === 'forward') {
      const firstPleatInnerX = fCclX + depth;
      const firstPleatCreaseX = fCclX;

      pleatLines.push(
        { x1: firstPleatInnerX, y1: waistY, x2: firstPleatInnerX, y2: crotchY, type: 'first-pleat-inner' },
        { x1: firstPleatCreaseX, y1: waistY, x2: firstPleatCreaseX, y2: crotchY, type: 'first-pleat-crease' },
      );

      if (normalizedPleatCount === 2) {
        const secondPleatStartX = firstPleatCreaseX - spacing;
        const secondPleatOuterX = secondPleatStartX - secondaryPleatDepth;

        pleatLines.push(
          { x1: secondPleatStartX, y1: waistY, x2: secondPleatStartX, y2: hipY, type: 'second-pleat-start' },
          { x1: secondPleatOuterX, y1: waistY, x2: secondPleatOuterX, y2: hipY, type: 'second-pleat-outer' },
        );
      }
    } else {
      const firstPleatStartX = fCclX;
      const firstPleatOuterX = firstPleatStartX - depth;

      pleatLines.push(
        { x1: firstPleatStartX, y1: waistY, x2: firstPleatStartX, y2: crotchY, type: 'first-pleat-start' },
        { x1: firstPleatOuterX, y1: waistY, x2: firstPleatOuterX, y2: crotchY, type: 'first-pleat-outer' },
      );

      if (normalizedPleatCount === 2) {
        const secondPleatStartX = firstPleatOuterX - spacing;
        const secondPleatOuterX = secondPleatStartX - secondaryPleatDepth;

        pleatLines.push(
          { x1: secondPleatStartX, y1: waistY, x2: secondPleatStartX, y2: hipY, type: 'second-pleat-start' },
          { x1: secondPleatOuterX, y1: waistY, x2: secondPleatOuterX, y2: hipY, type: 'second-pleat-outer' },
        );
      }
    }
  }

  pleatLines.forEach((line, index) => {
    const startPoint = applyPointDeltaInPlace(`PL${index + 1}A`, { x: line.x1, y: line.y1 });
    const endPoint = applyPointDeltaInPlace(`PL${index + 1}B`, { x: line.x2, y: line.y2 });
    line.x1 = startPoint.x;
    line.y1 = startPoint.y;
    line.x2 = endPoint.x;
    line.y2 = endPoint.y;
  });

  const distValue = (p1, p2) => Math.hypot(p2.x - p1.x, p2.y - p1.y);
  const dist = (p1, p2) => distValue(p1, p2).toFixed(2);

  const bezierLengthValue = (p0, p1, p2) => quadraticCurveLength(p0, p1, p2, 24);

  const bezierLength = (p0, p1, p2) => bezierLengthValue(p0, p1, p2).toFixed(2);

  const lenFCrotchTop = Math.hypot(pF_Hip_CF.x - pF_Waist_CF.x, pF_Hip_CF.y - pF_Waist_CF.y);
  const fCrotchBezierLen = bezierLength(
    pF_Hip_CF,
    frontCrotchCurveCtrl,
    pF_Crotch_Tip,
  );
  const totalFCrotch = (lenFCrotchTop + parseFloat(fCrotchBezierLen)).toFixed(2);

  const lenBCrotchTop = supportsBackWaistCenterCurve
    ? quadraticCurveLength(pB_Hip_CB, backWaistCenterCurveCtrl, pB_Waist_CB, 24)
    : Math.hypot(pB_Hip_CB.x - pB_Waist_CB.x, pB_Hip_CB.y - pB_Waist_CB.y);
  const bCrotchBezierLen = bezierLength(pB_Hip_CB, cpBackCrotch, pB_Crotch_Tip);
  const totalBCrotch = (lenBCrotchTop + parseFloat(bCrotchBezierLen)).toFixed(2);

  const frontInseamEndPoint = isShortMode ? pF_Hem_R : pF_Knee_R;
  const backInseamEndPoint = isShortMode ? pB_Hem_R : pB_Knee_R;
  const valFInseam = dist(pF_Crotch_Tip, frontInseamEndPoint);
  const valBInseam = dist(pB_Crotch_Tip, backInseamEndPoint);

  const frontSideWaistToHip = distValue(pF_Waist_Side, pF_Hip_Side);
  const frontSideHipToThigh = distValue(pF_Hip_Side, pF_Crotch_Side);
  const frontSideThighToKnee = distValue(pF_Crotch_Side, pF_Knee_L);

  const backSideWaistToHip = distValue(pB_Waist_Side, pB_Hip_Side);
  const backSideHipToThigh = distValue(pB_Hip_Side, pB_Crotch_Side);
  const backSideThighToKnee = distValue(pB_Crotch_Side, pB_Knee_L);
  const frontSideWaistToHipCurve = quadraticCurveLength(frontPleatNeutralWaistSide, frontWaistToHipSideCtrl, frontPleatNeutralHipSide);
  const frontSideHipToThighCurve = quadraticCurveLength(frontPleatNeutralHipSide, frontSeatToThighSideCtrl, frontPleatNeutralCrotchSide);
  const frontSideThighToKneeCurve = cubicCurveLength(frontPleatNeutralCrotchSide, frontThighToKneeSideCtrlStart, frontThighToKneeSideCtrlEnd, frontPleatNeutralKneeLeft);
  const backSideWaistToHipCurve = quadraticCurveLength(pB_Waist_Side, backWaistToHipSideCtrl, pB_Hip_Side);
  const backSideHipToThighCurve = quadraticCurveLength(pB_Hip_Side, backSeatToThighSideCtrl, pB_Crotch_Side);
  const backSideThighToKneeCurve = cubicCurveLength(pB_Crotch_Side, backThighToKneeSideCtrlStart, backThighToKneeSideCtrlEnd, pB_Knee_L);
  const frontSideKneeToHem = distValue(pF_Knee_L, pF_Hem_L);
  const backSideKneeToHem = distValue(pB_Knee_L, pB_Hem_L);
  const frontInseamKneeToHem = distValue(pF_Knee_R, pF_Hem_R);
  const backInseamKneeToHem = distValue(pB_Knee_R, pB_Hem_R);
  const frontSideThighToHem = distValue(pF_Crotch_Side, pF_Hem_L);
  const backSideThighToHem = distValue(pB_Crotch_Side, pB_Hem_L);
  const frontInseamCrotchToHem = distValue(pF_Crotch_Tip, pF_Hem_R);
  const backInseamCrotchToHem = distValue(pB_Crotch_Tip, pB_Hem_R);
  const frontWaistMeasurementSidePoint = pleatEnabled ? resolvedFrontNeutralWaistSide : pF_Waist_Side;
  const frontWaistMeasurementCfPoint = pleatEnabled ? resolvedFrontNeutralWaistCf : pF_Waist_CF;
  const frontHipMeasurementSidePoint = pleatEnabled ? resolvedFrontNeutralHipSide : pF_Hip_Side_Measure;
  const frontHipMeasurementCfPoint = pleatEnabled ? resolvedFrontNeutralHipCf : pF_Hip_CF;
  const frontThighMeasurementSidePoint = pleatEnabled ? resolvedFrontNeutralCrotchSide : pF_Crotch_Side_Measure;
  const frontThighMeasurementCfPoint = pleatEnabled ? resolvedFrontNeutralCrotchTip : pF_Crotch_Tip;
  const frontKneeMeasurementSidePoint = pleatEnabled ? resolvedFrontNeutralKneeLeft : pF_Knee_L_Measure;
  const frontKneeMeasurementCfPoint = pleatEnabled ? resolvedFrontNeutralKneeRight : pF_Knee_R;
  const frontWaistDrawn = distValue(frontWaistMeasurementSidePoint, frontWaistMeasurementCfPoint);
  const backWaistGrossDrawn = distValue(pB_Waist_Side, pB_Waist_CB);
  const dartIntake = dartsEnabled ? Number(backDartWidth || 0) : 0;
  const backWaistNetDrawn = Math.max(0, backWaistGrossDrawn - dartIntake);

  const constructionLines = [
    { p1: frontHipMeasurementSidePoint, p2: frontHipMeasurementCfPoint, label: 'Hip Level' },
    { p1: frontThighMeasurementSidePoint, p2: frontThighMeasurementCfPoint, label: 'Crotch Level' },
    { p1: pB_Hip_Side, p2: pB_Hip_CB, label: 'Hip Level' },
    { p1: pB_Crotch_Side, p2: pB_Crotch_Tip, label: 'Crotch Level' },
    ...(!isShortMode ? [
      { p1: frontKneeMeasurementSidePoint, p2: frontKneeMeasurementCfPoint, label: 'Knee Level' },
      { p1: pB_Knee_L, p2: pB_Knee_R, label: 'Knee Level' },
    ] : []),
  ];

  const frontMeasurements = [
    { label: 'FW', p1: frontWaistMeasurementSidePoint, p2: frontWaistMeasurementCfPoint, value: frontWaistDrawn.toFixed(2), offset: 0.62 },
    { label: 'FH', p1: frontHipMeasurementSidePoint, p2: frontHipMeasurementCfPoint, value: dist(frontHipMeasurementSidePoint, frontHipMeasurementCfPoint), offset: 0.78, referenceLine: true },
    { label: 'FT', p1: frontThighMeasurementSidePoint, p2: frontThighMeasurementCfPoint, value: dist(frontThighMeasurementSidePoint, frontThighMeasurementCfPoint), offset: 0.94, referenceLine: true },
    ...(!isShortMode ? [{ label: 'FK', p1: frontKneeMeasurementSidePoint, p2: frontKneeMeasurementCfPoint, value: dist(frontKneeMeasurementSidePoint, frontKneeMeasurementCfPoint), offset: 1.1, referenceLine: true }] : []),
    { label: 'FHem', p1: pF_Hem_L, p2: pF_Hem_R, value: dist(pF_Hem_L, pF_Hem_R), offset: 1.26 },
    { label: 'FC', p1: pF_Waist_CF, p2: pF_Crotch_Tip, value: totalFCrotch, offset: 2 },
    { label: 'FI', p1: pF_Crotch_Tip, p2: frontInseamEndPoint, value: valFInseam, offset: 1, showLabel: false },
    { label: 'F-S WH', p1: pF_Waist_Side, p2: pF_Hip_Side, value: frontSideWaistToHip.toFixed(2), offset: 0.46, referenceLine: true, showLabel: false },
    { label: 'F-S HT', p1: pF_Hip_Side, p2: pF_Crotch_Side, value: frontSideHipToThigh.toFixed(2), offset: 0.64, referenceLine: true, showLabel: false },
    { label: isShortMode ? 'F-S TH' : 'F-S TK', p1: pF_Crotch_Side, p2: isShortMode ? pF_Hem_L : pF_Knee_L, value: (isShortMode ? frontSideThighToHem : frontSideThighToKnee).toFixed(2), offset: 0.82, referenceLine: true, showLabel: false },
    ...(!isShortMode ? [
      { label: 'F-S KH', p1: pF_Knee_L, p2: pF_Hem_L, value: frontSideKneeToHem.toFixed(2), offset: 0.98, referenceLine: true, showLabel: false },
      { label: 'F-I KH', p1: pF_Knee_R, p2: pF_Hem_R, value: frontInseamKneeToHem.toFixed(2), offset: 1.14, referenceLine: true, showLabel: false },
    ] : []),
  ];

  const backMeasurements = [
    { label: 'BW', p1: pB_Waist_Side, p2: pB_Waist_CB, value: backWaistNetDrawn.toFixed(2), offset: -0.22 },
    { label: 'BH', p1: pB_Hip_Side, p2: pB_Hip_CB, value: dist(pB_Hip_Side, pB_Hip_CB), offset: -0.38, referenceLine: true },
    { label: 'BT', p1: pB_Crotch_Side, p2: pB_Crotch_Tip, value: dist(pB_Crotch_Side, pB_Crotch_Tip), offset: -0.54, referenceLine: true },
    ...(!isShortMode ? [{ label: 'BK', p1: pB_Knee_L, p2: pB_Knee_R, value: dist(pB_Knee_L, pB_Knee_R), offset: -0.7, referenceLine: true }] : []),
    { label: 'BHem', p1: pB_Hem_L, p2: pB_Hem_R, value: dist(pB_Hem_L, pB_Hem_R), offset: -0.86 },
    { label: 'BC', p1: pB_Waist_CB, p2: pB_Crotch_Tip, value: totalBCrotch, offset: 2 },
    { label: 'BI', p1: pB_Crotch_Tip, p2: backInseamEndPoint, value: valBInseam, offset: 0.34, showLabel: false },
    { label: 'B-S WH', p1: pB_Waist_Side, p2: pB_Hip_Side, value: backSideWaistToHip.toFixed(2), offset: -0.02, referenceLine: true, showLabel: false },
    { label: 'B-S HT', p1: pB_Hip_Side, p2: pB_Crotch_Side, value: backSideHipToThigh.toFixed(2), offset: -0.2, referenceLine: true, showLabel: false },
    { label: isShortMode ? 'B-S TH' : 'B-S TK', p1: pB_Crotch_Side, p2: isShortMode ? pB_Hem_L : pB_Knee_L, value: (isShortMode ? backSideThighToHem : backSideThighToKnee).toFixed(2), offset: -0.38, referenceLine: true, showLabel: false },
    ...(!isShortMode ? [
      { label: 'B-S KH', p1: pB_Knee_L, p2: pB_Hem_L, value: backSideKneeToHem.toFixed(2), offset: -0.54, referenceLine: true, showLabel: false },
      { label: 'B-I KH', p1: pB_Knee_R, p2: pB_Hem_R, value: backInseamKneeToHem.toFixed(2), offset: -0.7, referenceLine: true, showLabel: false },
    ] : []),
  ];

  const waistHalfNetDrawn = frontWaistDrawn + backWaistNetDrawn;
  const waistFullNetDrawn = waistHalfNetDrawn * 2;

  const seatFullDrawn = (distValue(frontHipMeasurementSidePoint, frontHipMeasurementCfPoint) + distValue(pB_Hip_Side, pB_Hip_CB)) * 2;
  const frontThighDrawn = distValue(frontThighMeasurementSidePoint, frontThighMeasurementCfPoint);
  const backThighDrawn = distValue(pB_Crotch_Side, pB_Crotch_Tip);
  const thighDrawn = frontThighDrawn + backThighDrawn;
  const frontKneeDrawn = distValue(frontKneeMeasurementSidePoint, frontKneeMeasurementCfPoint);
  const backKneeDrawn = distValue(pB_Knee_L, pB_Knee_R);
  const kneeDrawn = frontKneeDrawn + backKneeDrawn;
  const frontHemDrawn = distValue(pF_Hem_L, pF_Hem_R);
  const backHemDrawn = distValue(pB_Hem_L, pB_Hem_R);
  const hemDrawn = frontHemDrawn + backHemDrawn;

  const frontRiseDrawn = pF_Waist_CF.y - pF_Crotch_Tip.y;
  const backRiseDrawn = pB_Waist_CB.y - pB_Crotch_Tip.y;
  const frontOutseamDrawn = pF_Waist_Side.y - pF_Hem_L.y;
  const backOutseamDrawn = pB_Waist_Side.y - pB_Hem_L.y;
  const backHipCbBaseToFrontHipCfOffset = backHipCbBaseX - pF_Hip_CF.x;
  const backHipCbToFrontHipCfOffset = pB_Hip_CB.x - pF_Hip_CF.x;

  const matchingSummary = {
    waistDrawnNetFull: Number(waistFullNetDrawn.toFixed(3)),
    waistDeviation: Number((waistFullNetDrawn - Number(waist || 0)).toFixed(3)),
    seatDrawnFull: Number(seatFullDrawn.toFixed(3)),
    seatDeviation: Number((seatFullDrawn - Number(seat || 0)).toFixed(3)),
    thighDrawn: Number(thighDrawn.toFixed(3)),
    frontThighDrawn: Number(frontThighDrawn.toFixed(3)),
    backThighDrawn: Number(backThighDrawn.toFixed(3)),
    thighDeviation: Number((thighDrawn - Number(thigh || 0)).toFixed(3)),
    kneeDrawn: Number(kneeDrawn.toFixed(3)),
    frontKneeDrawn: Number(frontKneeDrawn.toFixed(3)),
    backKneeDrawn: Number(backKneeDrawn.toFixed(3)),
    kneeDeviation: Number((kneeDrawn - Number(knee || 0)).toFixed(3)),
    hemDrawn: Number(hemDrawn.toFixed(3)),
    frontHemDrawn: Number(frontHemDrawn.toFixed(3)),
    backHemDrawn: Number(backHemDrawn.toFixed(3)),
    hemDeviation: Number((hemDrawn - Number(hem || 0)).toFixed(3)),
    frontRiseDrawn: Number(frontRiseDrawn.toFixed(3)),
    backRiseDrawn: Number(backRiseDrawn.toFixed(3)),
    frontOutseamDrawn: Number(frontOutseamDrawn.toFixed(3)),
    backOutseamDrawn: Number(backOutseamDrawn.toFixed(3)),
    backHipCbBaseToFrontHipCfOffset: Number(backHipCbBaseToFrontHipCfOffset.toFixed(3)),
    backHipCbToFrontHipCfOffset: Number(backHipCbToFrontHipCfOffset.toFixed(3)),
    frontSideWaistToHip: Number(frontSideWaistToHip.toFixed(3)),
    backSideWaistToHip: Number(backSideWaistToHip.toFixed(3)),
    sideWaistToHipDelta: Number((frontSideWaistToHip - backSideWaistToHip).toFixed(3)),
    frontSideWaistToHipCurve: Number(frontSideWaistToHipCurve.toFixed(3)),
    backSideWaistToHipCurve: Number(backSideWaistToHipCurve.toFixed(3)),
    sideWaistToHipCurveDelta: Number((frontSideWaistToHipCurve - backSideWaistToHipCurve).toFixed(3)),
    frontSideHipToThigh: Number(frontSideHipToThigh.toFixed(3)),
    backSideHipToThigh: Number(backSideHipToThigh.toFixed(3)),
    sideHipToThighDelta: Number((frontSideHipToThigh - backSideHipToThigh).toFixed(3)),
    frontSideHipToThighCurve: Number(frontSideHipToThighCurve.toFixed(3)),
    backSideHipToThighCurve: Number(backSideHipToThighCurve.toFixed(3)),
    sideHipToThighCurveDelta: Number((frontSideHipToThighCurve - backSideHipToThighCurve).toFixed(3)),
    frontSideThighToKnee: Number(frontSideThighToKnee.toFixed(3)),
    backSideThighToKnee: Number(backSideThighToKnee.toFixed(3)),
    sideThighToKneeDelta: Number((frontSideThighToKnee - backSideThighToKnee).toFixed(3)),
    frontSideThighToKneeCurve: Number(frontSideThighToKneeCurve.toFixed(3)),
    backSideThighToKneeCurve: Number(backSideThighToKneeCurve.toFixed(3)),
    sideThighToKneeCurveDelta: Number((frontSideThighToKneeCurve - backSideThighToKneeCurve).toFixed(3)),
    frontSideKneeToHem: Number(frontSideKneeToHem.toFixed(3)),
    backSideKneeToHem: Number(backSideKneeToHem.toFixed(3)),
    sideKneeToHemDelta: Number((frontSideKneeToHem - backSideKneeToHem).toFixed(3)),
    frontInseamKneeToHem: Number(frontInseamKneeToHem.toFixed(3)),
    backInseamKneeToHem: Number(backInseamKneeToHem.toFixed(3)),
    inseamKneeToHemDelta: Number((frontInseamKneeToHem - backInseamKneeToHem).toFixed(3)),
    frontSideThighToHem: Number(frontSideThighToHem.toFixed(3)),
    backSideThighToHem: Number(backSideThighToHem.toFixed(3)),
    sideThighToHemDelta: Number((frontSideThighToHem - backSideThighToHem).toFixed(3)),
    sideThighToHemCurveDelta: Number((frontSideThighToKneeCurve - backSideThighToKneeCurve).toFixed(3)),
    frontInseamCrotchToHem: Number(frontInseamCrotchToHem.toFixed(3)),
    backInseamCrotchToHem: Number(backInseamCrotchToHem.toFixed(3)),
    inseamCrotchToHemDelta: Number((frontInseamCrotchToHem - backInseamCrotchToHem).toFixed(3)),
    trouserLengthMode: normalizeTrouserLengthMode(trouserLengthMode),
  };

  const curveHandles = [
    {
      id: 'front-waist-hip-side-ctrl',
      curveId: 'front-waist-hip-side-curve',
      label: 'Front Waist→Hip Side',
      xKey: 'frontWaistToHipSideCtrlDx',
      yKey: 'frontWaistToHipSideCtrlDy',
      point: frontWaistToHipSideCtrl,
      segmentStart: pF_Waist_Side,
      segmentEnd: pF_Hip_Side,
      color: '#1e3a8a',
    },
    {
      id: 'back-waist-hip-side-ctrl',
      curveId: 'back-waist-hip-side-curve',
      label: 'Back Waist→Hip Side',
      xKey: 'backWaistToHipSideCtrlDx',
      yKey: 'backWaistToHipSideCtrlDy',
      point: backWaistToHipSideCtrl,
      segmentStart: pB_Waist_Side,
      segmentEnd: pB_Hip_Side,
      color: '#dc2626',
    },
    {
      id: 'front-seat-thigh-side-ctrl',
      curveId: 'front-seat-thigh-side-curve',
      label: 'Front Seat→Thigh Side',
      xKey: 'frontSeatToThighSideCtrlDx',
      yKey: 'frontSeatToThighSideCtrlDy',
      point: frontSeatToThighSideCtrl,
      segmentStart: pF_Hip_Side,
      segmentEnd: pF_Crotch_Side,
      color: '#1e3a8a',
    },
    {
      id: 'back-seat-thigh-side-ctrl',
      curveId: 'back-seat-thigh-side-curve',
      label: 'Back Seat→Thigh Side',
      xKey: 'backSeatToThighSideCtrlDx',
      yKey: 'backSeatToThighSideCtrlDy',
      point: backSeatToThighSideCtrl,
      segmentStart: pB_Hip_Side,
      segmentEnd: pB_Crotch_Side,
      color: '#dc2626',
    },
    {
      id: 'front-thigh-knee-side-ctrl-start',
      curveId: 'front-thigh-knee-side-curve',
      label: isShortMode ? 'Front Thigh→Hem Side Start' : 'Front Thigh→Knee Side Start',
      xKey: 'frontThighToKneeSideCtrlStartDx',
      yKey: 'frontThighToKneeSideCtrlStartDy',
      point: frontThighToKneeSideCtrlStart,
      segmentStart: pF_Crotch_Side,
      segmentEnd: pF_Knee_L,
      color: '#1e3a8a',
    },
    {
      id: 'front-thigh-knee-side-ctrl-end',
      curveId: 'front-thigh-knee-side-curve',
      label: isShortMode ? 'Front Thigh→Hem Side End' : 'Front Thigh→Knee Side End',
      xKey: 'frontThighToKneeSideCtrlEndDx',
      yKey: 'frontThighToKneeSideCtrlEndDy',
      point: frontThighToKneeSideCtrlEnd,
      segmentStart: pF_Crotch_Side,
      segmentEnd: pF_Knee_L,
      color: '#1e3a8a',
    },
    {
      id: 'back-thigh-knee-side-ctrl-start',
      curveId: 'back-thigh-knee-side-curve',
      label: isShortMode ? 'Back Thigh→Hem Side Start' : 'Back Thigh→Knee Side Start',
      xKey: 'backThighToKneeSideCtrlStartDx',
      yKey: 'backThighToKneeSideCtrlStartDy',
      point: backThighToKneeSideCtrlStart,
      segmentStart: pB_Crotch_Side,
      segmentEnd: pB_Knee_L,
      color: '#dc2626',
    },
    {
      id: 'back-thigh-knee-side-ctrl-end',
      curveId: 'back-thigh-knee-side-curve',
      label: isShortMode ? 'Back Thigh→Hem Side End' : 'Back Thigh→Knee Side End',
      xKey: 'backThighToKneeSideCtrlEndDx',
      yKey: 'backThighToKneeSideCtrlEndDy',
      point: backThighToKneeSideCtrlEnd,
      segmentStart: pB_Crotch_Side,
      segmentEnd: pB_Knee_L,
      color: '#dc2626',
    },
    {
      id: 'front-inseam-thigh-knee-ctrl',
      curveId: 'front-inseam-thigh-knee-curve',
      label: isShortMode ? 'Front Inseam Crotch→Hem' : 'Front Inseam Thigh→Knee',
      xKey: 'frontInseamThighToKneeCtrlDx',
      yKey: 'frontInseamThighToKneeCtrlDy',
      point: frontInseamThighToKneeCtrl,
      segmentStart: pF_Knee_R,
      segmentEnd: pF_Crotch_Tip,
      color: '#1e3a8a',
    },
    {
      id: 'back-inseam-thigh-knee-ctrl',
      curveId: 'back-inseam-thigh-knee-curve',
      label: isShortMode ? 'Back Inseam Crotch→Hem' : 'Back Inseam Thigh→Knee',
      xKey: 'backInseamThighToKneeCtrlDx',
      yKey: 'backInseamThighToKneeCtrlDy',
      point: backInseamThighToKneeCtrl,
      segmentStart: pB_Knee_R,
      segmentEnd: pB_Crotch_Tip,
      color: '#dc2626',
    },
    {
      id: 'front-crotch-curve-ctrl',
      curveId: 'front-crotch-curve',
      label: 'Front Crotch Curve',
      xKey: 'frontCrotchCurveCtrlDx',
      yKey: 'frontCrotchCurveCtrlDy',
      point: frontCrotchCurveCtrl,
      segmentStart: pF_Crotch_Tip,
      segmentEnd: pF_Hip_CF,
      color: '#1e3a8a',
    },
    {
      id: 'back-crotch-curve-ctrl',
      curveId: 'back-crotch-curve',
      label: 'Back Crotch Curve',
      xKey: 'backCrotchCurveCtrlDx',
      yKey: 'backCrotchCurveCtrlDy',
      point: cpBackCrotch,
      segmentStart: pB_Crotch_Tip,
      segmentEnd: pB_Hip_CB,
      color: '#dc2626',
    },
    ...(supportsBackWaistCenterCurve ? [{
      id: 'back-waist-center-curve-ctrl',
      curveId: 'back-waist-center-curve',
      label: 'Back Waist Center→Seat',
      xKey: 'backWaistCenterCurveCtrlDx',
      yKey: 'backWaistCenterCurveCtrlDy',
      point: backWaistCenterCurveCtrl,
      segmentStart: pB_Hip_CB,
      segmentEnd: pB_Waist_CB,
      color: '#dc2626',
    }] : []),
  ];

  const curveSegments = [
    {
      id: 'front-waist-hip-side-curve',
      label: 'Front Waist→Hip Side',
      d: `M ${toSVG(pF_Waist_Side)} Q ${toSVG(frontWaistToHipSideCtrl)} ${toSVG(pF_Hip_Side)}`,
      color: '#1e3a8a',
    },
    {
      id: 'back-waist-hip-side-curve',
      label: 'Back Waist→Hip Side',
      d: `M ${toSVG(pB_Waist_Side)} Q ${toSVG(backWaistToHipSideCtrl)} ${toSVG(pB_Hip_Side)}`,
      color: '#dc2626',
    },
    {
      id: 'front-seat-thigh-side-curve',
      label: 'Front Seat→Thigh Side',
      d: `M ${toSVG(pF_Hip_Side)} Q ${toSVG(frontSeatToThighSideCtrl)} ${toSVG(pF_Crotch_Side)}`,
      color: '#1e3a8a',
    },
    {
      id: 'back-seat-thigh-side-curve',
      label: 'Back Seat→Thigh Side',
      d: `M ${toSVG(pB_Hip_Side)} Q ${toSVG(backSeatToThighSideCtrl)} ${toSVG(pB_Crotch_Side)}`,
      color: '#dc2626',
    },
    {
      id: 'back-thigh-knee-side-curve',
      label: isShortMode ? 'Back Thigh→Hem Side' : 'Back Thigh→Knee Side',
      d: `M ${toSVG(pB_Crotch_Side)} C ${toSVG(backThighToKneeSideCtrlStart)} ${toSVG(backThighToKneeSideCtrlEnd)} ${toSVG(pB_Knee_L)}`,
      color: '#dc2626',
    },
    {
      id: 'front-thigh-knee-side-curve',
      label: isShortMode ? 'Front Thigh→Hem Side' : 'Front Thigh→Knee Side',
      d: `M ${toSVG(pF_Crotch_Side)} C ${toSVG(frontThighToKneeSideCtrlStart)} ${toSVG(frontThighToKneeSideCtrlEnd)} ${toSVG(pF_Knee_L)}`,
      color: '#1e3a8a',
    },
    {
      id: 'front-inseam-thigh-knee-curve',
      label: isShortMode ? 'Front Inseam Crotch→Hem' : 'Front Inseam Thigh→Knee',
      d: `M ${toSVG(pF_Knee_R)} Q ${toSVG(frontInseamThighToKneeCtrl)} ${toSVG(pF_Crotch_Tip)}`,
      color: '#1e3a8a',
    },
    {
      id: 'back-inseam-thigh-knee-curve',
      label: isShortMode ? 'Back Inseam Crotch→Hem' : 'Back Inseam Thigh→Knee',
      d: `M ${toSVG(pB_Knee_R)} Q ${toSVG(backInseamThighToKneeCtrl)} ${toSVG(pB_Crotch_Tip)}`,
      color: '#dc2626',
    },
    {
      id: 'front-crotch-curve',
      label: 'Front Crotch Curve',
      d: `M ${toSVG(pF_Crotch_Tip)} Q ${toSVG(frontCrotchCurveCtrl)} ${toSVG(pF_Hip_CF)}`,
      color: '#1e3a8a',
    },
    {
      id: 'back-crotch-curve',
      label: 'Back Crotch Curve',
      d: `M ${toSVG(pB_Crotch_Tip)} Q ${toSVG(cpBackCrotch)} ${toSVG(pB_Hip_CB)}`,
      color: '#dc2626',
    },
    ...(supportsBackWaistCenterCurve ? [{
      id: 'back-waist-center-curve',
      label: 'Back Waist Center→Seat',
      d: `M ${toSVG(pB_Hip_CB)} Q ${toSVG(backWaistCenterCurveCtrl)} ${toSVG(pB_Waist_CB)}`,
      color: '#dc2626',
    }] : []),
  ];

  const namedPoints = [
    { name: 'FWC', piece: 'front', ...pF_Waist_CF },
    { name: 'FWS', piece: 'front', ...pF_Waist_Side },
    { name: 'FCP', piece: 'front', ...pF_Waist_CP },
    { name: 'FHS', piece: 'front', ...pF_Hip_Side },
    { name: 'FHC', piece: 'front', ...pF_Hip_CF },
    { name: 'FCS', piece: 'front', ...pF_Crotch_Side },
    { name: 'FCT', piece: 'front', ...pF_Crotch_Tip },
    ...(!isShortMode ? [
      { name: 'FKL', piece: 'front', ...pF_Knee_L },
      { name: 'FKR', piece: 'front', ...pF_Knee_R },
    ] : []),
    { name: 'FHL', piece: 'front', ...pF_Hem_L },
    { name: 'FHR', piece: 'front', ...pF_Hem_R },
    { name: 'FCLW', piece: 'front', ...pF_Crease_Waist },
    { name: 'FCLH', piece: 'front', ...pF_Crease_Hem },

    { name: 'BWC', piece: 'back', ...pB_Waist_CB },
    { name: 'BWS', piece: 'back', ...pB_Waist_Side },
    { name: 'BHS', piece: 'back', ...pB_Hip_Side },
    { name: 'BHC', piece: 'back', ...pB_Hip_CB },
    { name: 'BCS', piece: 'back', ...pB_Crotch_Side },
    { name: 'BCT', piece: 'back', ...pB_Crotch_Tip },
    ...(!isShortMode ? [
      { name: 'BKL', piece: 'back', ...pB_Knee_L },
      { name: 'BKR', piece: 'back', ...pB_Knee_R },
    ] : []),
    { name: 'BHL', piece: 'back', ...pB_Hem_L },
    { name: 'BHR', piece: 'back', ...pB_Hem_R },
    { name: 'BCLW', piece: 'back', ...pB_Crease_Waist },
    { name: 'BCLH', piece: 'back', ...pB_Crease_Hem },
  ];

  if (dartsEnabled && darts[0]) {
    namedPoints.push(
      { name: 'BD1', piece: 'back', ...darts[0].leg1 },
      { name: 'BD2', piece: 'back', ...darts[0].leg2 },
      { name: 'BDT1', piece: 'back', ...darts[0].tip },
    );
  }

  if (dartsEnabled && twoDartsEnabled && darts[1]) {
    namedPoints.push(
      { name: 'BD3', piece: 'back', ...darts[1].leg1 },
      { name: 'BD4', piece: 'back', ...darts[1].leg2 },
      { name: 'BDT2', piece: 'back', ...darts[1].tip },
    );
  }

  if (pleatEnabled && pleatLines.length > 0) {
    pleatLines.forEach((line, index) => {
      namedPoints.push(
        { name: `PL${index + 1}A`, piece: 'front', x: line.x1, y: line.y1 },
        { name: `PL${index + 1}B`, piece: 'front', x: line.x2, y: line.y2 },
      );
    });
  }

  const grandTotalCrotch = (parseFloat(totalFCrotch) + parseFloat(totalBCrotch)).toFixed(2);

  return {
    frontPath,
    backPath,
    fCreasePath,
    bCreasePath,
    dartPath,
    frontMeasurements,
    backMeasurements,
    curveSegments,
    curveHandles,
    constructionLines,
    pleatLines,
    pleatConfig: {
      enabled: pleatEnabled,
      direction: resolvedPleatDirection,
      count: pleatEnabled ? normalizedPleatCount : 0,
      depth: primaryPleatDepth,
      secondDepth: secondaryPleatDepth,
      secondPleatSpacing: Number(secondPleatSpacing) || 0,
      sideExtension: Number(sidePleatExtension.toFixed(3)),
      centerExtension: Number(centerPleatExtension.toFixed(3)),
    },
    matchingSummary,
    namedPoints,
    backDartConfig: {
      mode: resolvedBackDartMode,
      enabled: dartsEnabled,
      useTwoDarts: twoDartsEnabled,
      totalWidth: backDartWidth,
      depth: backDartDepth,
      spacing: resolvedBackDartSpacing,
    },
    hemShoeShapeEnabled: shoeShapeActive,
    hemStepValue: resolvedHemStepValue,
    grandTotalCrotch,
    draftingStatus: 'ready',
    garmentType: 'trouser',
    formulaVersion,
    freeModeActive,
    pointDeltas: freePointDeltas,
  };
}

export function generateTrouserPatternSIyV2(measurements, controls = {}) {
  return generateTrouserPatternCore(measurements, controls, {
    decoupleFrontThighFromCrease: true,
    supportsPleats: false,
    formulaVersion: TROUSER_V2_FORMULA_VERSION,
  });
}

export function generateTrouserPatternSIyV3(measurements, controls = {}) {
  return generateTrouserPatternCore(measurements, controls, {
    decoupleFrontThighFromCrease: true,
    supportsPleats: true,
    formulaVersion: TROUSER_V3_FORMULA_VERSION,
    cbSeatBaselineX: -0.75,
    backThighOffsetFromHipSide: true,
  });
}

export function generateTrouserPatternSIyV4(measurements, controls = {}) {
  return generateTrouserPatternCore(measurements, controls, {
    decoupleFrontThighFromCrease: true,
    supportsPleats: true,
    formulaVersion: TROUSER_ACTIVE_FORMULA_VERSION,
    frontThighOffsetFromHipSideExact: Boolean(measurements?.frontThighSideOffsetExact),
    integrateBackDartsIntoOutline: true,
    hemShoeShapeSupported: true,
    useV4BackSeamWalkSolver: true,
  });
}
